import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Users, Crosshair, ArrowRight, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

const FACTION_COLOR = { VALKYRA: "#E4443A", LONESTAR: "#3498DB", MANTICORE: "#2ECC71" };
const FACTION_LOGO = { VALKYRA: "/factions/valkyra.webp", LONESTAR: "/factions/lonestar.webp", MANTICORE: "/factions/manticore.webp" };

const ServerPulse = ({ collapsed }) => {
  const [pulse, setPulse] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => api.get("/server/pulse").then((r) => setPulse(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  if (!pulse) return null;
  const online = pulse.online;
  const reconnecting = pulse.reconnecting;
  const active = online || reconnecting;
  const dotColor = online ? "#2ECC71" : reconnecting ? "#FFB703" : "#E4443A";
  const tf = pulse.top_fragger;
  const tfColor = (tf && FACTION_COLOR[tf.team]) || "#D4A94A";
  const leader = pulse.leader;
  const leaderColor = (leader && FACTION_COLOR[leader.team]) || "#D4A94A";
  const membersOnline = pulse.members_online || 0;

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => navigate("/live")}
      onKeyDown={(e) => { if (e.key === "Enter") navigate("/live"); }}
      data-testid="home-server-pulse"
      className={`fixed top-16 left-0 right-0 z-40 block border-b border-[#D4A94A]/20 bg-[#0B0C0E]/95 backdrop-blur-md group cursor-pointer transition-[padding] duration-300 ease-out ${collapsed ? "lg:pl-16" : "lg:pl-60"}`}
    >
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-11 flex items-center gap-3 sm:gap-5 overflow-hidden">
        {/* live dot */}
        <span className="relative flex h-2.5 w-2.5 shrink-0" data-testid="pulse-status-dot">
          {active && <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70" style={{ background: dotColor }} />}
          <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: dotColor }} />
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest shrink-0 hidden sm:inline" style={{ color: reconnecting ? "#FFB703" : "#64748B" }}>
          {online ? "Live" : reconnecting ? "Reconnecting" : "Offline"}
        </span>

        {active ? (
          <div className="flex-1 flex items-center gap-3 sm:gap-5 min-w-0 overflow-hidden">
            {/* map */}
            <span className="inline-flex items-center gap-1.5 font-mono text-xs text-[#94A3B8] shrink-0" data-testid="pulse-map">
              <MapPin className="w-3.5 h-3.5 text-[#D4A94A]" />
              <span className="text-[#F1F5F9] font-bold uppercase tracking-wide">{pulse.map || "In Match"}</span>
            </span>
            <span className="h-3 w-px bg-[#2A2E3D] shrink-0" />
            {/* players */}
            <span className="inline-flex items-center gap-1.5 font-mono text-xs text-[#94A3B8] shrink-0" data-testid="pulse-players">
              <Users className="w-3.5 h-3.5 text-[#D4A94A]" />
              <span className="text-[#F1F5F9] font-bold tabular-nums">{pulse.players}</span>
              <span className="text-[#64748B]">/{pulse.max_players}</span>
            </span>
            {/* leading faction / score race */}
            {leader && (
              <>
                <span className="h-3 w-px bg-[#2A2E3D] shrink-0" />
                <span className="inline-flex items-center gap-1.5 font-mono text-xs shrink-0" data-testid="pulse-leader" title={`${leader.team} leading`}>
                  <img src={FACTION_LOGO[leader.team]} alt="" className="w-4 h-4 object-contain shrink-0" />
                  <span className="font-bold uppercase tracking-wide hidden sm:inline" style={{ color: leaderColor }}>{leader.team}</span>
                  <span className="text-[#F1F5F9] font-bold tabular-nums">{leader.score}</span>
                  <span className="text-[#64748B] hidden sm:inline">/100</span>
                </span>
              </>
            )}
            {/* members online now */}
            {membersOnline > 0 && (
              <>
                <span className="h-3 w-px bg-[#2A2E3D] shrink-0 hidden sm:block" />
                <span className="hidden sm:inline-flex items-center gap-1.5 font-mono text-xs shrink-0" data-testid="pulse-members-online">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#D4A94A]" />
                  <span className="text-[#D4A94A] font-bold tabular-nums">{membersOnline}</span>
                  <span className="text-[#64748B]">OCE DOG{membersOnline > 1 ? "S" : ""} online</span>
                </span>
              </>
            )}
            {/* top fragger */}
            {tf && (
              <>
                <span className="h-3 w-px bg-[#2A2E3D] shrink-0 hidden md:block" />
                <span className="hidden md:inline-flex items-center gap-1.5 font-mono text-xs min-w-0" data-testid="pulse-top-fragger">
                  <Crosshair className="w-3.5 h-3.5 shrink-0" style={{ color: tfColor }} />
                  <span className="text-[#64748B] hidden sm:inline shrink-0">Top fragger</span>
                  {tf.user_id ? (
                    <Link
                      to={`/u/${tf.user_id}`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid="pulse-top-fragger-link"
                      className="font-bold uppercase tracking-wide truncate hover:underline"
                      style={{ color: tfColor }}
                    >
                      {tf.callsign}
                    </Link>
                  ) : (
                    <span className="font-bold uppercase tracking-wide truncate" style={{ color: tfColor }}>{tf.callsign}</span>
                  )}
                  <span className="text-[#F1F5F9] font-bold tabular-nums shrink-0">{tf.kills}</span>
                  <span className="text-[#64748B] shrink-0 hidden sm:inline">kills</span>
                </span>
              </>
            )}
          </div>
        ) : (
          <div className="flex-1 font-mono text-xs text-[#94A3B8] truncate" data-testid="pulse-offline">
            {pulse.name || "OCE DOGS Server"} <span className="text-[#64748B]">· waking on connect</span>
          </div>
        )}

        <span className="ml-auto shrink-0 inline-flex items-center gap-1 font-stencil font-bold uppercase text-[10px] tracking-widest text-[#D4A94A] group-hover:text-[#E5BD60] transition-colors">
          <span className="hidden sm:inline">Watch live</span>
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </div>
  );
};

export default ServerPulse;
